var _ = require('underscore/underscore-min');
var Utils = require('core-helpers/utils.js');
var ExceptionService = require('exceptions/exception-service.js');
var reqwest = require('reqwest/reqwest.min');
var endpoints = require('endpoint/endpoint-service.js');
var SharedDataService = require('data/shared-data-service.js');
var PlansEdit = require('premium/plans-edit-service.js');
var Q = require('q');

/**
 * All the service APIs related to payments should
 * be accessible from here
 */
var PurchaseService = (function(root, doc, undefined) {

    'use strict';

    var logError = function(message, data) {
        ExceptionService.handle('warning', {filename, message, data });
    };

    /**
     * By default, a user can't subscribe twice. The backend blocks that.
     * The parameter `bypass=true` on backend allows a user to subscribe multiple times.
     * Caution: Should not be used on production and testing, it's a local testing feature.
     */
    var forceSubscriptionEndpoint = function(url) {
        if (url.indexOf('?') !== -1) {
            return url + '&bypass=true';
        }
        return url + '?bypass=true';
    };

    /**
     * Returns an Array with the current running experiments
     * variations IDs. We will then pass this array as a String
     * to the availablePlans endpoint using the query parameter `variants`.
     * @return {Array}
     */
    var getExperimentVariations = function() {

        /**
         * Just set an empty array if there is no
         * experiments running.
         */
        var experiments = [];

        return _.reduce(experiments, function(previous, experiment) {
            if (experiment && experiment.variationId) {
                previous.push(experiment.variationId);
            }
            return previous;
        }, []);

    };

    /**
     * This method returns an endpoint URL with a coupon if available
     * or if some experiments are running.
     * This method is available for the availablePlans and SEPA endpoints.
     * @return {String}
     */
    var getDiscountedEndpoint = function(endpoint, endpointWithCoupon, endpointWithVariant, endpointWithVariantAndDiscount) {

        /**
         * Default endpoint, there is no discount or no variants.
         */
        var _url = endpoints.generateEndpoint(endpoint);

        /**
         * If a discount has been found, coupon will be added.
         */
        var discount = SharedDataService.getDiscountCoupon();
        if (endpointWithCoupon && discount) {
            _url = endpoints.generateEndpoint(endpointWithCoupon);
            _url = _url.replace('{coupon}', discount);
        }

        /**
         * If some experiments are running, variants will be added.
         */
        var experimentsVariations = getExperimentVariations();
        if (endpointWithVariant && experimentsVariations.length) {
            _url = endpoints.generateEndpoint(endpointWithVariant);
            _url = _url.replace('{variantsIds}', experimentsVariations.join(','));
        }

        /**
         * In both cases (discount AND experiments), we add both parameters.
         */
        if (endpointWithVariantAndDiscount && discount && experimentsVariations.length) {
            _url = endpoints.generateEndpoint(endpointWithVariantAndDiscount);
            _url = _url.replace('{coupon}', discount);
            _url = _url.replace('{variantsIds}', experimentsVariations.join(','));
        }

        return _url;

    };

    /**
     * Remove extra plans. It's used because back-end returns 5 plans
     * (1,3,6,12,24 months) and we want to provide
     * For ONE_MONTH_PLAN_COUNTRIES: 1,6,12,24 months plan
     * All other countries: 3,6,12,24 months plan
     * @param {Array} plans
     * @param {String} country
     */
    var removeExtraPlans = function(plans, country = null) {
        const monthToRemove = Boolean(ONE_MONTH_PLAN_COUNTRIES.indexOf(country) !== -1) ? 3 : 1;
        return PlansEdit.removeMonthPlan(plans, monthToRemove);
    };

    /**
     * Returns all the plans. If user is detected as authenticated
     * by the backend, it will return the mandatory data to process a payment.
     * @return {Promise}
     */
    var getPlans = function() {

        // If request has been started already, return promise to avoid multiple requests
        if (getPlansDeferred) {
            return getPlansDeferred.promise;
        }

        // Store defer instance
        getPlansDeferred = Q.defer();

        var onSuccess = function(response) {
            if ((response.status === 'ok') && (response.data)) {
                response.data.plans = removeExtraPlans(response.data.plans, response.data.country);
                getPlansDeferred.resolve(response.data);
            } else {
                onError(response);
            }
        };

        var onError = function(error) {
            logError('Fail to retrieve available plans', error);
            getPlansDeferred.reject(error);
        };

        var _url = getDiscountedEndpoint('availablePlans', 'availablePlansCoupon', 'availablePlansVariants', 'availablePlansVariantsCoupon');

        reqwest({
            url: _url,
            method: 'get',
            type: 'json',
            contentType: 'application/json',
            withCredentials: true
        }).then(onSuccess, onError);

        return getPlansDeferred.promise;

    };

    /**
     * Returns tracking informations based on a
     * given busuu transactionId and planId
     * @return {Promise}
     */
    var getTracking = function(busuuTransactionId, planId) {

        var deferred = Q.defer();

        if (!busuuTransactionId || !planId) {
            logError('getTracking() - busuuTransactionId or planId are missing', {
                busuuTransactionId: busuuTransactionId,
                planId: planId
            });
            deferred.reject({
                type: 'PAYMENT_TRACKING_FAILURE',
                message: 'params are missing'
            });
            return deferred.promise;
        }

        var onSuccess = function(response) {
            if ((response.status === 'ok') && (response.data)) {
                deferred.resolve(response.data);
            } else {
                onError({
                    message: 'bad response format',
                    error: response
                });
            }
        };

        var onError = function(error) {

            var json, message, code = null;

            try {
                json = JSON.parse(error.responseText);
                code = json.applicationCode;
                message = (Array.isArray(json.errors) && json.errors.length) ? json.errors[0] : json.errors;
            } catch (e) {}

            code = code || error.status || 'Unknown code';
            message = message || error.statusText || 'Unknown message';

            logError('getTracking() error: ' + message, error);

            deferred.reject({
                type: 'PAYMENT_TRACKING_FAILURE',
                issue: code,
                message: message
            });

        };

        var _url = endpoints.generateEndpoint('paymentTracking');
        _url = _url.replace('{busuuTransactionId}', busuuTransactionId).replace('{planId}', planId);

        reqwest({
            url: _url,
            method: 'get',
            type: 'json',
            contentType: 'application/json',
            withCredentials: true
        }).then(onSuccess, onError);

        return deferred.promise;

    };

    /**
     * Sepa Iframe Setup:
     * Returns an object with datas needed to render
     * the Zuora Iframe for Sepa payments
     * @return {Promise}
     */
    var getSepaIframeSetup = function() {

        var deferred = Q.defer();

        var onSuccess = function(response) {
            if ((response.status === 'ok') && (response.data)) {
                deferred.resolve(response.data);
            } else {
                onError(response);
            }
        };

        var onError = function(error) {

            var message = null;
            var code = error.status || 'Unknown code';
            var issue = '[' + code + ' - SP(BS)]';

            try {
                message = JSON.parse(error.responseText).errors;
            } catch (e) {}

            if (!message) {
                message = error.statusText || '';
            }

            logError('getSepaIframeSetup() error: ' + issue + ' - ' + message, error);

            deferred.reject({
                type: 'SEPA_IFRAME_SETUP_FAILURE',
                issue: issue,
                message: message
            });

        };

        var _url = endpoints.generateEndpoint('paymentSepaIframe');
        reqwest({
            url: _url,
            method: 'get',
            type: 'json',
            contentType: 'application/json',
            withCredentials: true
        }).then(onSuccess, onError);

        return deferred.promise;

    };

    /**
     * After Zuora account is returned from
     * the iframe we need to call this endpoint to
     * pass the reference id to the backend to process the payment
     * @param  {Object} params
     * @return {Promise}
     */
    var processSepaPayment = function(params) {

        var deferred = Q.defer();

        if (!params || !params.ref_id || !params.product_plan_id) {
            logError('processSepaPayment() - params are missing', {
                params: params
            });
            deferred.reject({
                type: 'SEPA_PROCESS_FAILURE',
                issue: '[SP(BP-FE)]',
                message: 'params are missing'
            });
            return deferred.promise;
        }

        var onSuccess = function(response) {
            if (response && (response.status === 'ok') && (response.data)) {
                deferred.resolve(response.data);
            } else {
                onError(response);
            }
        };

        var onError = function(error) {

            var json, message, code, issue, type = null;

            try {
                json = JSON.parse(error.responseText);
                code = json.applicationCode;
                message = (Array.isArray(json.errors) && json.errors.length) ? json.errors[0] : json.errors;
            } catch (e) {}

            code = code || error.status || 'Unknown code';
            message = message || error.statusText || 'Unknown message';

            switch (code) {
                case 'TRANSACTION_NOT_FOUND':
                    type = 'SEPA_TRANSACTION_NOT_FOUND';
                    issue = 'SP(BP-SE)';
                    break;
                default:
                    type = 'SEPA_PROCESS_FAILURE';
                    issue = 'SP(BP)';
            }

            issue = '[' + code + ' - ' + issue + ']';
            logError('processSepaPayment() error: ' + issue + ' - ' + message, error);

            deferred.reject({
                type: type,
                issue: issue,
                message: message
            });

        };

        var _url = getDiscountedEndpoint('paymentSepaProcess', 'paymentSepaProcessDiscount');
        if (Utils.isLocalEnvironment()) {
            _url = forceSubscriptionEndpoint(_url);
        }

        reqwest({
            url: _url,
            method: 'post',
            type: 'json',
            contentType: 'application/json',
            withCredentials: true,
            data: JSON.stringify(params)
        }).then(onSuccess, onError);

        return deferred.promise;

    };

    /**
     * Returns a given discount
     * @param  {String} coupon
     * @return {Promise}
     */
    var getDiscount = function(coupon) {

        var deferred = Q.defer();

        var getDiscountFromPlans = function(plans) {
            var higherDiscountPlan = _.max(plans, 'discountPercentage');
            return (higherDiscountPlan && Utils.isObject(higherDiscountPlan)) ? higherDiscountPlan.discountPercentage : null;
        };

        var onSuccess = function(response) {

            var discountValue = null;
            if ((response.status === 'ok') && (response.data)) {
                discountValue = getDiscountFromPlans(response.data.plans);
            }

            if (discountValue) {
                return deferred.resolve({
                    discount: discountValue,
                    coupon: coupon
                });
            }

            onError({
                type: 'DISCOUNT_NOT_FOUND',
                message: 'No suitable discount found for coupon: "' + coupon + '"',
                error: response
            });

        };

        var onError = function(error) {

            var type = error.type || 'DISCOUNT_FAILURE';
            var message = error.message || 'An error has occured while retrieving a discount.';

            logError('getDiscount() error: ' + type + ' - ' + message, error);
            deferred.reject({
                type: type,
                message: message
            });

        };

        coupon = coupon ? Utils.clean(coupon) : null;
        if (!coupon) {
            onError({
                type: 'DISCOUNT_MISSING_COUPON',
                message: 'coupon is missing'
            });
            return deferred.promise;
        }

        var _url = endpoints.generateEndpoint('availablePlansCoupon');
        _url = _url.replace('{coupon}', coupon);

        reqwest({
            url: _url,
            method: 'get',
            type: 'json',
            contentType: 'application/json',
            withCredentials: true
        }).then(onSuccess, onError);

        return deferred.promise;

    };

    /**
     *
     * Check if current session (in time) is still available.
     * This service avoid a user being lazy between sessions (/premium and /purchase).
     * The session is being checked before accessing the cart or
     * on the first request to the backend (getBackendToken() - only for CC Method).
     *
     * This endpoint also returns the zuora Object needed for the CC
     * method payment on the account creation and subscription. (createZuoraSubscription()).
     *
     * @param  {Integer} busuuTransactionId
     * @param  {Integer} planId
     * @return {Promise}
     *
     */
    var isSessionAvailable = function(busuuTransactionId, planId) {

        var deferred = Q.defer();

        if (!busuuTransactionId || !planId) {
            logError('isSessionAvailable() - busuuTransactionId or planId is missing', {
                busuuTransactionId: busuuTransactionId,
                planId: planId
            });
            deferred.reject({
                type: 'SESSION_EXPIRED_FAILURE',
                issue: '[SE(FE)]',
                message: 'params are missing'
            });
            return deferred.promise;
        }

        var onSuccess = function(response) {
            if (response && (response.status === 'ok') && (response.data) && (response.data.zuora)) {
                deferred.resolve(response.data.zuora);
            } else {
                onError(response);
            }
        };

        var onError = function(error) {

            var json, message, code, issue = null;

            try {
                json = JSON.parse(error.responseText);
                code = json.applicationCode;
                message = (Array.isArray(json.errors) && json.errors.length) ? json.errors[0] : json.errors;
            } catch (e) {}

            code = code || error.status || 'Unknown code';
            message = message || error.statusText || 'Unknown message';
            issue = '[' + code + ' - SE]';

            logError('isSessionAvailable() error: ' + issue + ' - ' + message, error);

            deferred.reject({
                type: 'SESSION_EXPIRED_FAILURE',
                issue: issue,
                message: message
            });

        };

        var _url = endpoints.generateEndpoint('paymentSessionAvailable');
        _url = _url.replace('{busuuTransactionId}', busuuTransactionId).replace('{planId}', planId);

        reqwest({
            url: _url,
            method: 'get',
            type: 'json',
            contentType: 'application/json',
            withCredentials: true
        }).then(onSuccess, onError);

        return deferred.promise;

    };

    /**
     * Retrieve a token in the backend to
     * open a payment in Zuora for the Credit Card Payment Method.
     * This service also check the current session of the user. See isSessionAvailable().
     * @param  {Object} params
     * @return {Promise}
     */
    var getBackendToken = function(params) {

        var deferred = Q.defer();

        if (!params) {
            logError('getBackendToken() - params are missing', {
                params: params
            });
            deferred.reject({
                type: 'CC_TOKEN_FAILURE',
                issue: '[CC(BT-FE)]',
                message: 'params are missing'
            });
            return deferred.promise;
        }

        var onSuccess = function(response) {
            var datas = response.data;
            if ((response.status === 'ok') && (datas && datas.success)) {
                deferred.resolve(response);
            } else {
                onError(response);
            }
        };

        var onError = function(error) {

            var json, message, code, issue, type = null;

            try {
                json = JSON.parse(error.responseText);
                code = json.applicationCode;
                message = (Array.isArray(json.errors) && json.errors.length) ? json.errors[0] : json.errors;
            } catch (e) {}

            code = code || error.status || 'Unknown code';
            message = message || error.statusText || 'Unknown message';

            switch (code) {
                case 'TRANSACTION_NOT_FOUND':
                    type = 'CC_TRANSACTION_NOT_FOUND';
                    issue = 'CC(BT-SE)';
                    break;
                default:
                    type = 'CC_TOKEN_FAILURE';
                    issue = 'CC(BT)';
            }

            issue = '[' + code + ' - ' + issue + ']';
            logError('getBackendToken() error: ' + issue + ' - ' + message, error);

            deferred.reject({
                type: type,
                issue: issue,
                message: message
            });

        };

        var _url = endpoints.generateEndpoint('paymentSignature');
        if (Utils.isLocalEnvironment()) {
            _url = forceSubscriptionEndpoint(_url);
        }

        reqwest({
            url: _url,
            method: 'post',
            type: 'json',
            contentType: 'application/json',
            withCredentials: true,
            data: JSON.stringify(params)
        }).then(onSuccess, onError);

        return deferred.promise;

    };

    /**
     * Returns a specific case error depending
     * on Zuora error type.
     * N.B. '10000' in Zuora is the default code when
     * error can't be authenticated, (e.g. fraud, insufficient balance, etc)
     * @param  {String} zuoraCode
     * @return {String}
     */
    var getZuoraErrorType = function(zuoraCode) {
        switch (zuoraCode) {
            case '10002':
            case '10107':
            case '10109':
            case '10301':
                return 'CC_INVALID_CARD_NAME';
            case '10201':
                return 'CC_INVALID_CARD_TYPE';
            case '10202':
                return 'CC_INVALID_CARD_NUMBER';
            case '10203':
            case '10204':
            case '10206':
                return 'CC_CARD_INFORMATION_INVALID';
            case '10205':
                return 'CC_CARD_CVV_INVALID';
            default:
                return 'CC_PAYMENT_FAILURE_CARD';
        }
    };

    /**
     * Creates a new account and open a new subscription in Zuora.
     * After this request is success, the user has paid.
     * @param  {String} signature
     * @param  {String} token
     * @param  {Object} params
     * @return {Promise}
     */
    var createZuoraSubscription = function(signature, token, params) {

        var deferred = Q.defer();

        if (!signature || !token || !params) {
            logError('createZuoraSubscription() - signature, token or params is missing', {
                signature: signature,
                token: token,
                params: params
            });
            deferred.reject({
                type: 'PAYMENT_FAILURE_CARD',
                issue: '[CC(ZA-FE)]',
                message: 'signature, token or params is missing'
            });
            return deferred.promise;
        }

        var onSuccess = function(response) {
            if (response.success) {
                deferred.resolve(response);
            } else {
                onError(response);
            }
        };

        /**
         * Zuora Errors
         * Source: https://knowledgecenter.zuora.com/DC_Developers/REST_API/A_REST_basics/3_Responses_and_errors
         */
        var onError = function(error) {

            /**
             * Try to find the 'reason' object from the error
             * Can be two cases:
             * 1) Request is 200 but some params are missing
             * 2) Request is a failure
             */
            var reason = null;
            try {
                reason = (error.reasons && error.reasons[0]); // (1)
                if (!reason) {
                    reason = JSON.parse(error.response).reasons[0]; // (2)
                }
            } catch (e) {}

            if (!reason) {
                reason = {};
            }

            var code = reason.code || error.status || 'Unknown code';
            var message = reason.message || error.statusText || '';
            var issue = '[' + code + ' - CC(ZA)]';
            var zuoraCode = code.toString().substring(1, 6);
            var type = getZuoraErrorType(zuoraCode);

            logError('createZuoraSubscription() error: ' + issue + ' - ' + message, error);

            deferred.reject({
                type: type,
                issue: issue,
                message: message
            });

        };

        var _url = endpoints.getAbsoluteEndpoint('zuoraAPI');
        reqwest({
            url: _url,
            method: 'post',
            type: 'json',
            contentType: 'application/json',
            crossOrigin: true,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'signature': signature,
                'token': token
            },
            data: JSON.stringify(params)
        }).then(onSuccess, onError);

        return deferred.promise;

    };

    /**
     * Last Backend request, this one process
     * the subscription in the backend after the subscription is success in Zuora.
     * It makes the user premium in busuu.
     * @param  {Object} params
     * @return {Promise}
     */
    var processPostSubscription = function(params) {

        var deferred = Q.defer();

        if (!params) {
            logError('processPostSubscription() - params are missing', {
                params: params
            });
            deferred.reject({
                type: 'CC_POST_SUBSCRIPTION_FAILURE',
                issue: '[CC(BP-FE)]',
                message: 'params are missing'
            });
            return deferred.promise;
        }

        var onSuccess = function(response) {
            var datas = response.data;
            if ((response.status === 'ok') && datas) {
                deferred.resolve(datas);
            } else {
                onError(response);
            }
        };

        var onError = function(error) {

            var json, message, code, issue = null;

            try {
                json = JSON.parse(error.responseText);
                code = json.applicationCode;
                message = (Array.isArray(json.errors) && json.errors.length) ? json.errors[0] : json.errors;
            } catch (e) {}

            code = code || error.status || 'Unknown code';
            message = message || error.statusText || 'Unknown message';
            issue = '[' + code + ' - CC(BP)]';

            logError('processPostSubscription() error: ' + issue + ' - ' + message, error);

            deferred.reject({
                type: 'CC_POST_SUBSCRIPTION_FAILURE',
                issue: issue,
                message: message
            });

        };

        var _url = endpoints.generateEndpoint('paymentAfterSubscriptionProcess');
        if (Utils.isLocalEnvironment()) {
            _url = forceSubscriptionEndpoint(_url);
        }

        reqwest({
            url: _url,
            method: 'post',
            type: 'json',
            contentType: 'application/json',
            withCredentials: true,
            data: JSON.stringify(params)
        }).then(onSuccess, onError);

        return deferred.promise;

    };

    /**
    * Returns the cheapest monthly plan from a list of plans
    * @param {array} allPlans - List of all available plans
    * @return {object} cheapest monthly plan from array (also search when discount is available)
    */
    var getCheapestPlan = function(allPlans) {
        if(!allPlans){
            return null;
        }
        return _.min(allPlans, function(data) {
            if (!data) {
                return null;
            }
            // If discount, return discounted amount
            if (data.afterDiscountMonthlyPrice) {
                return Number(data.afterDiscountMonthlyPrice.amount);
            }
            // Otherwise return normal amount
            return (data.plan && data.plan.monthlyPrice) ? Number(data.plan.monthlyPrice.amount) : null;
        });
    };

    /**
     * Private vars
     */
    var filename = 'purchase-service.js';
    var getPlansDeferred = null;
    var ONE_MONTH_PLAN_COUNTRIES = ['US', 'RU'];

    /**
     * Public methods
     */
    return {
        getPlans: getPlans,
        getDiscount: getDiscount,
        getTracking: getTracking,
        getBackendToken: getBackendToken,
        getCheapestPlan: getCheapestPlan,
        createZuoraSubscription: createZuoraSubscription,
        getSepaIframeSetup: getSepaIframeSetup,
        processPostSubscription: processPostSubscription,
        processSepaPayment: processSepaPayment,
        isSessionAvailable: isSessionAvailable
    };

})(window, document);

module.exports = PurchaseService;
